import { useMemo } from "react";
import styles from "./toolbox-components.module.css";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";

import { PosterProject } from "PosterEditor/model/poster-project";

interface ToolboxComponentPosterSizeIconProps {
  project: PosterProject;
  isSelected: boolean;
  onClick?: () => void;
}

export const ToolboxComponentPosterSizeIcon = (
  props: ToolboxComponentPosterSizeIconProps
) => {
  const { cssAspectRatio, sizePresetFromProject } = useSizePresets();

  interface Size {
    width: number;
    height: number;
  }

  const preset = sizePresetFromProject(props.project);

  const iconSize = useMemo((): Size => {
    const aspectRatio = cssAspectRatio(props.project);
    if (!aspectRatio) {
      return { width: 0, height: 0 };
    }
    if (!preset) {
      return { width: 0, height: 0 };
    }

    // Check if this is a custom preset (uses pixel dimensions)
    const customWidthPX = props.project.sizeOption.options.widthPX;
    const customHeightPX = props.project.sizeOption.options.heightPX;
    const isCustomPreset =
      (preset.widthInMM === 0 || !preset.widthInMM) &&
      customWidthPX !== undefined &&
      customHeightPX !== undefined;

    let size: Size;
    if (isCustomPreset) {
      // For custom presets, use pixel dimensions converted to MM (approximation)
      size = {
        width: customWidthPX! / 6.3, // Approximation: divide by 8 to get rough MM equivalent
        height: customHeightPX! / 6.3,
      };
    } else {
      size = {
        width: preset.widthInMM ?? 0,
        height: preset.heightInMM ?? 0,
      };
    }

    const largestSizeInMM = 508;
    const relativeWidth = Math.max(size.width, size.height) / largestSizeInMM;

    const largest = 200;
    if (aspectRatio > 1) {
      return {
        width: largest * relativeWidth,
        height: (largest / aspectRatio) * relativeWidth,
      };
    } else {
      return {
        width: largest * aspectRatio * relativeWidth,
        height: largest * relativeWidth,
      };
    }
  }, [props.project, cssAspectRatio, preset]);

  if (!preset) {
    return null;
  }

  // Check if this is a custom preset and show pixel dimensions
  const customWidthPX = props.project.sizeOption.options.widthPX;
  const customHeightPX = props.project.sizeOption.options.heightPX;
  const isCustomPreset =
    (preset.widthInMM === 0 || !preset.widthInMM) &&
    customWidthPX !== undefined &&
    customHeightPX !== undefined;

  const title = isCustomPreset
    ? `${customWidthPX} × ${customHeightPX} px`
    : preset.sizeDescription;

  return (
    <button
      onClick={props.onClick}
      className={`${styles.posterSizeIcon} ${
        props.isSelected ? styles.posterSizePresetIconSelected : ""
      }`}
      style={{
        width: iconSize.width,
        height: iconSize.height,
      }}
    >
      <span
        className={
          props.isSelected
            ? styles.posterSizePresetIconTitleSelected
            : styles.posterSizePresetIconTitle
        }
      >
        {title}
      </span>
    </button>
  );
};
